import { Risk, RiskStatus, RiskStatusLabel } from '@/types';

/**
 * Keyboard shortcut codes used for vulnerability status changes.
 */
export const VULNERABILITY_KEYBOARD_CODES = {
  TRIAGED: 'KeyT',
  OPENED: 'KeyO',
  REMEDIATED: 'KeyC',
  ACCEPTED: 'KeyA',
  DELETED: 'KeyR', // Shows delete/reject modal
} as const;

/**
 * Maps keyboard codes to their corresponding risk status values.
 */
export const KEYBOARD_CODE_TO_STATUS: Record<
  string,
  RiskStatus | 'remediated'
> = {
  [VULNERABILITY_KEYBOARD_CODES.TRIAGED]: RiskStatus.Triaged,
  [VULNERABILITY_KEYBOARD_CODES.OPENED]: RiskStatus.Opened,
  [VULNERABILITY_KEYBOARD_CODES.REMEDIATED]: RiskStatus.Remediated,
  [VULNERABILITY_KEYBOARD_CODES.ACCEPTED]: RiskStatus.Accepted,
  [VULNERABILITY_KEYBOARD_CODES.DELETED]: 'remediated', // Special case: triggers delete modal
} as const;

/**
 * Configuration for keyboard actions in the vulnerability table.
 */
export interface KeyboardActionConfig {
  altKey: boolean;
  codes: string[];
}

/**
 * Creates the keyboard action configuration for vulnerability table.
 *
 * @returns Configuration object for keyboard actions
 *
 * @example
 * <Table
 *   keyboardActions={getVulnerabilityKeyboardConfig()}
 *   onKeyboardAction={handleKeyboardAction}
 * />
 */
export function getVulnerabilityKeyboardConfig(): KeyboardActionConfig {
  return {
    altKey: true,
    codes: [
      VULNERABILITY_KEYBOARD_CODES.TRIAGED,
      VULNERABILITY_KEYBOARD_CODES.OPENED,
      VULNERABILITY_KEYBOARD_CODES.REMEDIATED,
      VULNERABILITY_KEYBOARD_CODES.ACCEPTED,
      VULNERABILITY_KEYBOARD_CODES.DELETED,
    ],
  };
}

/**
 * Gets the risk status corresponding to a keyboard code.
 *
 * @param code - The keyboard code (e.g., 'KeyT', 'KeyO')
 * @returns The corresponding RiskStatus or 'remediated' for delete action, undefined if invalid
 *
 * @example
 * getStatusFromKeyboardCode('KeyT') // RiskStatus.Triaged
 * getStatusFromKeyboardCode('KeyO') // RiskStatus.Opened
 * getStatusFromKeyboardCode('KeyR') // 'remediated' (triggers modal)
 */
export function getStatusFromKeyboardCode(
  code: string
): RiskStatus | 'remediated' | undefined {
  return KEYBOARD_CODE_TO_STATUS[code];
}

/**
 * Checks if a keyboard code should trigger the delete/reject modal.
 *
 * @param code - The keyboard code
 * @returns True if code should show delete modal
 *
 * @example
 * shouldShowDeleteModal('KeyR') // true
 * shouldShowDeleteModal('KeyO') // false
 */
export function shouldShowDeleteModal(code: string): boolean {
  return code === VULNERABILITY_KEYBOARD_CODES.DELETED;
}

/**
 * Gets the display label for a keyboard shortcut status.
 *
 * @param status - The risk status or 'remediated'
 * @returns User-friendly label for the status
 *
 * @example
 * getStatusLabel(RiskStatus.Opened) // 'Opened'
 * getStatusLabel('remediated') // 'Reject'
 */
export function getStatusLabel(status: RiskStatus | 'remediated'): string {
  if (status === 'remediated') {
    return 'Reject';
  }
  return RiskStatusLabel[status] || status;
}

/**
 * Validates if a keyboard action should be processed.
 *
 * @param code - The keyboard code
 * @param data - The selected risks data
 * @returns True if the action should be processed
 *
 * @example
 * isValidKeyboardAction('KeyT', [risk1, risk2]) // true
 * isValidKeyboardAction('KeyT', []) // false
 * isValidKeyboardAction('InvalidKey', [risk1]) // false
 */
export function isValidKeyboardAction(code: string, data: Risk[]): boolean {
  return !!(getStatusFromKeyboardCode(code) && data && data.length > 0);
}

/**
 * Creates a confirmation state object for keyboard-triggered status changes.
 *
 * @param code - The keyboard code
 * @param data - The risks to update
 * @returns Confirmation state object or null if invalid
 *
 * @example
 * const state = createConfirmationState('KeyO', [risk1, risk2]);
 * // Returns: { status: 'O', data: [risk1, risk2] }
 */
export function createConfirmationState(
  code: string,
  data: Risk[]
): { status: string; data: Risk[] } | null {
  const status = getStatusFromKeyboardCode(code);
  if (!status || data.length === 0) {
    return null;
  }

  return {
    status,
    data,
  };
}

/**
 * Gets human-readable keyboard shortcut hints.
 *
 * @returns Array of shortcut hints for display in UI
 *
 * @example
 * const hints = getKeyboardShortcutHints();
 * // Returns: [
 * //   { key: 'Alt+T', label: 'Triaged' },
 * //   { key: 'Alt+O', label: 'Opened' },
 * //   ...
 * // ]
 */
export function getKeyboardShortcutHints(): Array<{
  key: string;
  label: string;
}> {
  return [
    { key: 'Alt+T', label: 'Mark as Triaged' },
    { key: 'Alt+O', label: 'Mark as Opened' },
    { key: 'Alt+C', label: 'Mark as Remediated' },
    { key: 'Alt+A', label: 'Mark as Accepted' },
    { key: 'Alt+R', label: 'Delete/Reject' },
  ];
}
